import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  breed as runBreed,
  chickNarrative,
  shareByEmail,
  trackTrendingPair,
  fetchBudgiePortrait,
  userPhotoUrl,
  listUserPhotos,
  uploadUserPhoto,
  deleteUserPhoto,
} from "@/lib/api";
import { Sparkles, X, Loader2, AlertTriangle, Link2, Check, Mail, Camera, ChevronDown, Bird, Save, Upload, Trash2 } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import {
  LOCI_UI,
  PHENOTYPES,
  defaultOptions,
  applyPhenotype,
  findActiveOption,
  summarizePhenotype,
} from "@/lib/phenotypes";
import { useFlock, FREE_BIRD_LIMIT } from "@/lib/flock";
import { Link as RouterLink } from "react-router-dom";

// Inline brand glyphs for X (Twitter) and Reddit
const XLogo = (props) => (
  <svg viewBox="0 0 24 24" width={14} height={14} aria-hidden="true" {...props}>
    <path
      fill="currentColor"
      d="M18.244 2H21l-6.52 7.45L22 22h-6.79l-4.78-6.26L4.8 22H2l7.04-8.04L2 2h6.91l4.34 5.74L18.244 2Zm-2.38 18h1.62L8.22 4H6.5l9.364 16Z"
    />
  </svg>
);

const RedditLogo = (props) => (
  <svg viewBox="0 0 24 24" width={14} height={14} aria-hidden="true" {...props}>
    <path
      fill="currentColor"
      d="M22 12.07a2.13 2.13 0 0 0-3.6-1.54 10.6 10.6 0 0 0-5.5-1.7l.94-4.39 3.06.66a1.62 1.62 0 1 0 .19-.93l-3.43-.74a.47.47 0 0 0-.55.36L11.97 8.8a10.6 10.6 0 0 0-5.6 1.71 2.13 2.13 0 1 0-2.36 3.5 4 4 0 0 0-.05.62c0 3.26 3.78 5.9 8.45 5.9s8.45-2.64 8.45-5.9a4 4 0 0 0-.05-.61 2.13 2.13 0 0 0 1.19-1.94ZM7.49 13.5a1.5 1.5 0 1 1 1.5 1.5 1.5 1.5 0 0 1-1.5-1.5Zm8.1 4a5.6 5.6 0 0 1-3.6 1.1 5.6 5.6 0 0 1-3.6-1.1.45.45 0 1 1 .58-.69 4.78 4.78 0 0 0 3.02.86 4.78 4.78 0 0 0 3.02-.86.45.45 0 1 1 .58.69Zm-.58-2.5a1.5 1.5 0 1 1 1.5-1.5 1.5 1.5 0 0 1-1.5 1.5Z"
    />
  </svg>
);
import { toast } from "sonner";
import { usePro } from "@/lib/pro";
import Paywall from "@/components/Paywall";
import UnlockModal from "@/components/UnlockModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

// --- URL share-link helpers --------------------------------------------------
// Encode only non-default ("no") locus states into a compact string.
// Format per parent: "base:split,dark:single,ino:split"
function encodeParent(parent) {
  const entries = Object.entries(parent.options)
    .filter(([, v]) => v?.state && v.state !== "no")
    .map(([k, v]) => `${k}:${v.state}`);
  return entries.join(",");
}

function decodeParent(str, sex) {
  const options = {};
  LOCI_UI.forEach((l) => {
    options[l.id] = { state: "no" };
  });
  if (!str) return { sex, options };
  const validIds = new Set(LOCI_UI.map((l) => l.id));
  str.split(",").forEach((pair) => {
    const [k, v] = pair.split(":");
    if (!validIds.has(k) || !v) return;
    // Hens (ZW) cannot be "split" on sex-linked loci
    const locus = LOCI_UI.find((l) => l.id === k);
    if (locus?.sexLinked && sex === "female" && v === "split") return;
    options[k] = { state: v };
  });
  return { sex, options };
}

/**
 * Locus catalog (LOCI_UI), phenotype catalog (PHENOTYPES) and helpers
 * (defaultOptions, applyPhenotype, findActiveOption) live in
 * @/lib/phenotypes so the Flock page can reuse them.
 */

function PhenotypeCard({
  option,
  active,
  onClick,
  sex,
  testIdPrefix,
  categoryId,
  userPhotoEntry,
  onUploadPhoto,
  onDeletePhoto,
}) {
  const activeRing = sex === "female"
    ? "border-pink-500 ring-2 ring-pink-500"
    : "border-primary ring-2 ring-primary";
  const checkBg = sex === "female"
    ? "bg-pink-500 text-white"
    : "bg-primary text-primary-foreground";
  const fileRef = useRef(null);
  const hasPhoto = !!userPhotoEntry?.id;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onUploadPhoto?.(categoryId, option.id, file);
    e.target.value = ""; // allow re-uploading the same file later
  };

  const handleIconClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (hasPhoto) {
      // eslint-disable-next-line no-alert
      if (window.confirm(`Remove your photo for ${option.label}?`)) {
        onDeletePhoto?.(categoryId, option.id);
      }
    } else {
      fileRef.current?.click();
    }
  };

  return (
    <div
      className={[
        "shrink-0 w-[112px] border rounded-sm overflow-hidden text-left transition-all relative",
        active ? activeRing : "border-border hover:border-foreground/40",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={onClick}
        data-testid={`${testIdPrefix}-pheno-${categoryId}-${option.id}`}
        className="block w-full text-left"
      >
        <div
          className="relative aspect-square"
          style={{ backgroundColor: option.swatch }}
        >
          {hasPhoto ? (
            <img
              src={`${userPhotoUrl(userPhotoEntry.id)}?v=${userPhotoEntry.v}`}
              alt={`Your bird (${option.label})`}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          ) : null}
          {active && (
            <div className={`absolute top-1 right-1 rounded-full p-0.5 ${checkBg}`}>
              <Check size={10} />
            </div>
          )}
        </div>
        <div className="px-2 py-1.5 text-[11px] font-medium leading-tight">{option.label}</div>
      </button>
      {/* Photo upload/delete control — sibling of the select button to avoid
          nested <button>. Pro is guaranteed inside CalculatorInner. */}
      <span
        role="button"
        tabIndex={0}
        onClick={handleIconClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleIconClick(e);
        }}
        data-testid={`${testIdPrefix}-pheno-photo-${categoryId}-${option.id}`}
        title={hasPhoto ? `Remove photo for ${option.label}` : `Upload your own bird photo for ${option.label}`}
        aria-label={hasPhoto ? `Remove photo for ${option.label}` : `Upload photo for ${option.label}`}
        className={[
          "absolute top-1 left-1 rounded-full p-1 cursor-pointer transition-opacity",
          "bg-black/55 text-white hover:bg-black/80",
          "focus:outline-none focus:ring-2 focus:ring-white",
        ].join(" ")}
      >
        {hasPhoto ? <Trash2 size={10} /> : <Camera size={10} />}
      </span>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
        className="hidden"
        onChange={handleFile}
        data-testid={`${testIdPrefix}-pheno-photo-input-${categoryId}-${option.id}`}
      />
    </div>
  );
}

function PhenotypePicker({
  category,
  parent,
  setParent,
  sex,
  testIdPrefix,
  userPhotos,
  onUploadPhoto,
  onDeletePhoto,
}) {
  const activeId = findActiveOption(parent, category);
  return (
    <div className="px-5 py-4" data-testid={`${testIdPrefix}-pheno-${category.id}`}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <div className="text-sm font-medium tracking-tight">{category.label}</div>
        {category.help && (
          <div className="text-[10px] text-muted-foreground text-right max-w-[60%]">{category.help}</div>
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scroll-smooth">
        {category.options.map((opt) => {
          const key = `${category.id}.${opt.id}`;
          return (
            <PhenotypeCard
              key={opt.id}
              option={opt}
              active={activeId === opt.id}
              sex={sex}
              categoryId={category.id}
              testIdPrefix={testIdPrefix}
              userPhotoEntry={userPhotos?.[key]}
              onUploadPhoto={onUploadPhoto}
              onDeletePhoto={onDeletePhoto}
              onClick={() => setParent(applyPhenotype(parent, opt, sex))}
            />
          );
        })}
      </div>
    </div>
  );
}

function FlockBar({ sex, parent, setParent, testIdPrefix }) {
  const { birds, isPro, limitReached, create } = useFlock();
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveRing, setSaveRing] = useState("");
  const [saving, setSaving] = useState(false);

  const matchingBirds = useMemo(
    () => birds.filter((b) => b.sex === sex),
    [birds, sex],
  );

  const onLoad = (bird) => {
    if (!bird) return;
    setParent({ sex, options: bird.options || defaultOptions() });
    toast.success(`Loaded "${bird.name}"${bird.ring_number ? ` (${bird.ring_number})` : ""}`);
  };

  const onSave = async () => {
    if (!saveName.trim()) {
      toast.error("Please give your bird a name.");
      return;
    }
    setSaving(true);
    try {
      await create({
        name: saveName.trim(),
        ring_number: saveRing.trim() || null,
        sex,
        hatch_date: null,
        options: parent.options,
        notes: null,
      });
      toast.success(`Saved "${saveName.trim()}" to My Flock`);
      setSaveOpen(false);
      setSaveName("");
      setSaveRing("");
    } catch (e) {
      toast.error(e?.message || "Could not save bird.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="px-5 py-3 bg-secondary/40 border-b border-border flex flex-wrap items-center gap-2"
      data-testid={`${testIdPrefix}-flock-bar`}
    >
      <Bird size={14} className="text-muted-foreground" />
      <span className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">My Flock</span>

      {matchingBirds.length > 0 ? (
        <select
          data-testid={`${testIdPrefix}-flock-load`}
          defaultValue=""
          onChange={(e) => {
            const bird = matchingBirds.find((b) => b.id === e.target.value);
            onLoad(bird);
            e.target.value = "";
          }}
          className="border border-border bg-background px-2 py-1 text-xs rounded-sm max-w-[180px]"
        >
          <option value="" disabled>Load saved {sex === "male" ? "cock" : "hen"}…</option>
          {matchingBirds.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}{b.ring_number ? ` · ${b.ring_number}` : ""}
            </option>
          ))}
        </select>
      ) : (
        <span className="text-[11px] text-muted-foreground">
          No saved {sex === "male" ? "cocks" : "hens"} yet.
        </span>
      )}

      <button
        type="button"
        data-testid={`${testIdPrefix}-flock-save-btn`}
        onClick={() => setSaveOpen(true)}
        className="ml-auto inline-flex items-center gap-1 text-xs border border-border bg-background px-2.5 py-1 rounded-sm hover:bg-card transition-colors"
      >
        <Save size={12} /> Save current
      </button>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent
          data-testid={`${testIdPrefix}-flock-save-dialog`}
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle>Save this {sex === "male" ? "cock" : "hen"} to My Flock</DialogTitle>
            <DialogDescription>
              Give your bird a name and (optionally) the leg-band ring number so
              you can reload it for future pairings.
            </DialogDescription>
          </DialogHeader>
          {!isPro && limitReached && (
            <div className="bg-secondary border border-border rounded-sm px-3 py-2 text-xs">
              You&apos;ve hit the free limit of {FREE_BIRD_LIMIT} saved birds.{" "}
              <RouterLink to="/pro" className="text-primary underline">
                Upgrade to Pro
              </RouterLink>{" "}
              to save unlimited.
            </div>
          )}
          <div className="space-y-3 py-2">
            <label className="block text-xs uppercase tracking-wider text-muted-foreground">
              Name
              <input
                data-testid={`${testIdPrefix}-flock-save-name`}
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g. Bluey"
                maxLength={60}
                autoFocus
                className="mt-1 w-full border border-border bg-background px-3 py-2 rounded-sm text-sm normal-case tracking-normal"
              />
            </label>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground">
              Ring number <span className="lowercase tracking-normal text-muted-foreground/70">(optional)</span>
              <input
                data-testid={`${testIdPrefix}-flock-save-ring`}
                value={saveRing}
                onChange={(e) => setSaveRing(e.target.value)}
                placeholder="e.g. ABS-2024-1234"
                maxLength={40}
                className="mt-1 w-full border border-border bg-background px-3 py-2 rounded-sm text-sm normal-case tracking-normal font-mono"
              />
            </label>
            <div className="text-[11px] text-muted-foreground border border-border px-3 py-2 rounded-sm">
              Phenotype: {summarizePhenotype({ ...parent, sex })}
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setSaveOpen(false)}
              className="border border-border px-4 py-2 rounded-sm text-sm hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              data-testid={`${testIdPrefix}-flock-save-confirm`}
              type="button"
              disabled={saving || (!isPro && limitReached)}
              onClick={onSave}
              className="bg-foreground text-background px-4 py-2 rounded-sm text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {saving ? "Saving…" : "Save bird"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ParentEditor({
  sex,
  parent,
  setParent,
  testIdPrefix,
  userPhotos,
  onUploadPhoto,
  onDeletePhoto,
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const update = (locusId, value) => {
    setParent({
      ...parent,
      options: { ...parent.options, [locusId]: { state: value } },
    });
  };

  return (
    <div
      data-testid={`${testIdPrefix}-editor`}
      className="border border-border bg-card"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <div className="overline">{sex === "male" ? "Sire" : "Dam"}</div>
          <div className="font-bold text-lg tracking-tight">
            {sex === "male" ? "Cock ♂ (ZZ)" : "Hen ♀ (ZW)"}
          </div>
        </div>
        <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
          {sex === "male" ? "fa-le · 2 Z" : "fe-ma · 1 Z + W"}
        </div>
      </div>

      {/* Flock load/save bar */}
      <FlockBar sex={sex} parent={parent} setParent={setParent} testIdPrefix={testIdPrefix} />

      {/* Phenotype-first pickers */}
      <div className="divide-y divide-border">
        {PHENOTYPES.map((category) => (
          <PhenotypePicker
            key={category.id}
            category={category}
            parent={parent}
            setParent={setParent}
            sex={sex}
            userPhotos={userPhotos}
            onUploadPhoto={onUploadPhoto}
            onDeletePhoto={onDeletePhoto}
            testIdPrefix={testIdPrefix}
          />
        ))}
      </div>

      {/* Advanced genotype — collapsible locus rows for power users */}
      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="border-t border-border">
        <CollapsibleTrigger
          className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-secondary/50 transition-colors"
          data-testid={`${testIdPrefix}-advanced-toggle`}
        >
          <div>
            <div className="overline text-[9px]">// Advanced</div>
            <div className="text-sm font-medium">Genotype (locus-level)</div>
          </div>
          <ChevronDown
            size={16}
            className={`transition-transform ${advancedOpen ? "rotate-180" : ""}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="divide-y divide-border border-t border-border">
            {LOCI_UI.map((locus) => {
              const opts =
                locus.sexLinked && sex === "female"
                  ? locus.options.filter((o) => o.value !== "split")
                  : locus.options;
              return (
                <div
                  key={locus.id}
                  className="px-5 py-3 grid grid-cols-12 gap-3 items-center"
                  data-testid={`${testIdPrefix}-row-${locus.id}`}
                >
                  <div className="col-span-12 sm:col-span-5">
                    <div className="text-sm font-medium">{locus.label}</div>
                    {locus.help && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {locus.help}
                      </div>
                    )}
                  </div>
                  <div className="col-span-12 sm:col-span-7">
                    <div className="flex flex-wrap gap-1">
                      {opts.map((o) => {
                        const active = parent.options[locus.id]?.state === o.value;
                        const activeBg = sex === "female"
                          ? "bg-pink-500 text-white border-pink-500"
                          : "bg-primary text-primary-foreground border-primary";
                        return (
                          <button
                            key={o.value}
                            type="button"
                            data-testid={`${testIdPrefix}-${locus.id}-${o.value}`}
                            onClick={() => update(locus.id, o.value)}
                            className={[
                              "text-xs px-2.5 py-1.5 rounded-sm border transition-colors",
                              active ? activeBg : "border-border hover:bg-secondary",
                            ].join(" ")}
                          >
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function ParentSummaryCard({ role, summary, descriptor }) {
  const [portraitUrl, setPortraitUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  const isSire = role === "sire";

  const handle = async () => {
    if (portraitUrl || loading) return;
    setLoading(true);
    setErrored(false);
    try {
      const url = await fetchBudgiePortrait({ descriptor, kind: "parent" });
      setPortraitUrl(url);
    } catch {
      setErrored(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-border p-5">
      <div className="overline">{isSire ? "Sire phenotype" : "Dam phenotype"}</div>
      <div
        className="text-lg font-bold mt-1"
        data-testid={isSire ? "male-summary" : "female-summary"}
      >
        {summary}
      </div>
      <div className="mt-3 flex items-start gap-3">
        {portraitUrl ? (
          <img
            data-testid={`${isSire ? "male" : "female"}-portrait`}
            src={portraitUrl}
            alt={`AI portrait of ${summary}`}
            className="w-20 h-20 object-cover border border-border"
          />
        ) : (
          <button
            type="button"
            data-testid={`${isSire ? "male" : "female"}-portrait-btn`}
            onClick={handle}
            disabled={loading}
            className="w-20 h-20 border border-border grid place-items-center bg-secondary hover:bg-card transition-colors disabled:opacity-60"
            title="Generate AI parent portrait"
          >
            {loading ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <div className="flex flex-col items-center gap-1">
                <Camera size={16} />
                <span className="text-[9px] font-mono uppercase tracking-widest">AI</span>
              </div>
            )}
          </button>
        )}
        <div className="flex-1 text-xs text-muted-foreground leading-snug">
          {portraitUrl ? (
            <span>Photoreal portrait of this {isSire ? "cock" : "hen"} — generated by Gemini.</span>
          ) : loading ? (
            <span>Rendering… first render takes ~10s.</span>
          ) : errored ? (
            <span className="text-destructive">Portrait failed — tap to retry.</span>
          ) : (
            <span>Tap to render a photoreal portrait of this parent.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function OutcomeRow({ outcome, onAi, onPortrait, index }) {
  const [portraitUrl, setPortraitUrl] = useState(null);
  const [portraitLoading, setPortraitLoading] = useState(false);
  const [portraitError, setPortraitError] = useState(false);

  const handlePortrait = async () => {
    if (portraitUrl || portraitLoading) return;
    setPortraitLoading(true);
    setPortraitError(false);
    try {
      const url = await onPortrait(outcome);
      setPortraitUrl(url);
    } catch {
      setPortraitError(true);
    } finally {
      setPortraitLoading(false);
    }
  };

  return (
    <div
      className="border border-border bg-card p-4 flex flex-col gap-3 fade-up"
      style={{ animationDelay: `${index * 40}ms` }}
      data-testid={`outcome-${index}`}
    >
      <div className="flex items-center gap-3">
        {portraitUrl ? (
          <img
            data-testid={`outcome-portrait-${index}`}
            src={portraitUrl}
            alt={`AI portrait of ${outcome.phenotype}`}
            className="w-12 h-12 object-cover border border-border shrink-0"
          />
        ) : (
          <div
            className="w-12 h-12 border border-border shrink-0 relative grid place-items-center"
            style={{ background: outcome.swatch }}
            aria-hidden="true"
          >
            {portraitLoading && (
              <Loader2 className="animate-spin text-white drop-shadow" size={18} />
            )}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold tracking-tight">{outcome.phenotype}</div>
          <div className="font-mono text-[10px] text-muted-foreground truncate">
            {outcome.example_genotype}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-lg font-bold">
            {(outcome.probability * 100).toFixed(2)}%
          </div>
          <div className="overline">{outcome.sex}</div>
        </div>
      </div>
      <div className="h-1 bg-secondary rounded-sm overflow-hidden">
        <div
          className="h-full bg-primary"
          style={{ width: `${Math.min(100, outcome.probability * 100)}%` }}
        />
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid={`outcome-ai-${index}`}
          onClick={() => onAi(outcome)}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <Sparkles size={12} /> Chick notes
        </button>
        <button
          data-testid={`outcome-portrait-btn-${index}`}
          onClick={handlePortrait}
          disabled={portraitLoading || !!portraitUrl}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50 disabled:no-underline"
          title={portraitUrl ? "Portrait generated" : "Generate chick portrait"}
        >
          <Camera size={12} />{" "}
          {portraitLoading
            ? "Rendering…"
            : portraitUrl
            ? "Portrait ready"
            : "Chick portrait"}
        </button>
        {portraitError && (
          <span className="text-[10px] text-destructive">Portrait failed — retry?</span>
        )}
      </div>
    </div>
  );
}

function AiModal({ open, onClose, outcome, narrative, loading }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
      data-testid="ai-modal"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border max-w-2xl w-full p-6 lg:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="overline">AI lab note · Claude Sonnet</div>
            <h3 className="text-2xl font-bold tracking-tight mt-1">
              {outcome?.phenotype}
            </h3>
          </div>
          <button
            data-testid="ai-modal-close"
            onClick={onClose}
            className="border border-border w-8 h-8 grid place-items-center hover:bg-secondary"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-14 h-14 border border-border"
            style={{ background: outcome?.swatch }}
          />
          <div className="font-mono text-xs text-muted-foreground">
            {outcome?.example_genotype}
          </div>
        </div>
        {loading ? (
          <div className="flex items-center gap-3 text-muted-foreground">
            <Loader2 className="animate-spin" size={16} /> Writing field notes…
          </div>
        ) : (
          <p
            data-testid="ai-narrative"
            className="text-foreground whitespace-pre-line leading-relaxed"
          >
            {narrative}
          </p>
        )}
      </div>
    </div>
  );
}

export default function Calculator() {
  const { isPro, checking, unlock } = usePro();
  const [showUnlock, setShowUnlock] = useState(false);

  if (checking) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center text-muted-foreground">
        <Loader2 className="mx-auto animate-spin mb-3" size={24} />
        Checking your license…
      </div>
    );
  }

  if (!isPro) {
    return (
      <>
        <Paywall onUnlockClick={() => setShowUnlock(true)} />
        <UnlockModal
          open={showUnlock}
          onClose={() => setShowUnlock(false)}
          onUnlock={unlock}
        />
      </>
    );
  }

  return <CalculatorInner />;
}

function CalculatorInner() {
  const [male, setMale] = useState({ sex: "male", options: defaultOptions() });
  const [female, setFemale] = useState({ sex: "female", options: defaultOptions() });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Per-user phenotype photos: Pro users can attach their own bird photos
  // to any phenotype option. Map shape:
  //   { "shade.cobalt": { id: "publicId", v: <cache-bust value> }, ... }
  // The `v` value changes on every upload so a replaced photo (which keeps
  // the same public_id by design) busts the browser's 5-minute cache.
  const [userPhotos, setUserPhotos] = useState({});
  useEffect(() => {
    let cancelled = false;
    listUserPhotos()
      .then((photos) => {
        if (cancelled) return;
        // For photos loaded on mount, the publicId itself is a fine cache
        // key — nothing's been replaced this session.
        const normalised = Object.fromEntries(
          Object.entries(photos || {}).map(([k, id]) => [k, { id, v: id }])
        );
        setUserPhotos(normalised);
      })
      .catch(() => { /* swatches-only fallback is fine */ });
    return () => { cancelled = true; };
  }, []);

  const handleUploadPhoto = useCallback(async (categoryId, optionId, file) => {
    const key = `${categoryId}.${optionId}`;
    try {
      const resp = await uploadUserPhoto(key, file);
      // Bump `v` to Date.now() so the <img src> changes and the browser
      // re-fetches even though public_id stays the same on replace.
      setUserPhotos((prev) => ({
        ...prev,
        [key]: { id: resp.public_id, v: Date.now() },
      }));
      toast.success("Photo saved to your phenotype.");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    }
  }, []);

  const handleDeletePhoto = useCallback(async (categoryId, optionId) => {
    const key = `${categoryId}.${optionId}`;
    try {
      await deleteUserPhoto(key);
      setUserPhotos((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      toast.success("Photo removed.");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Delete failed";
      toast.error(typeof msg === "string" ? msg : "Delete failed");
    }
  }, []);

  const [aiOpen, setAiOpen] = useState(false);
  const [aiOutcome, setAiOutcome] = useState(null);
  const [aiNarrative, setAiNarrative] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [hydratedFromUrl, setHydratedFromUrl] = useState(false);

  // Share-by-email dialog state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const onRun = useCallback(async (maleArg, femaleArg) => {
    const m = maleArg ?? male;
    const f = femaleArg ?? female;
    setLoading(true);
    setError("");
    try {
      const data = await runBreed({ male: m, female: f });
      setResults(data);
      // Sync URL so the current view is shareable / refresh-safe
      const params = new URLSearchParams();
      params.set("m", encodeParent(m));
      params.set("f", encodeParent(f));
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, "", newUrl);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed");
      toast.error("Breeding calculation failed");
    } finally {
      setLoading(false);
    }
  }, [male, female]);

  // Hydrate from ?m=...&f=... share-link on first mount.
  // Wrapped in setTimeout(0) so the effect itself contains no synchronous
  // setState calls (satisfies react-hooks/set-state-in-effect).
  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      const m = params.get("m");
      const f = params.get("f");
      if (m === null && f === null) return;
      const nextMale = m !== null ? decodeParent(m, "male") : { sex: "male", options: defaultOptions() };
      const nextFemale = f !== null ? decodeParent(f, "female") : { sex: "female", options: defaultOptions() };
      setMale(nextMale);
      setFemale(nextFemale);
      setHydratedFromUrl(true);
      onRun(nextMale, nextFemale);
      try {
        const key = `budgie_trend_v1:${m || ""}|${f || ""}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          trackTrendingPair(m || "", f || "").catch(() => {});
        }
      } catch {
        trackTrendingPair(m || "", f || "").catch(() => {});
      }
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const onReset = () => {
    setMale({ sex: "male", options: defaultOptions() });
    setFemale({ sex: "female", options: defaultOptions() });
    setResults(null);
    window.history.replaceState(null, "", window.location.pathname);
  };

  const buildShareUrl = useCallback(() => {
    const params = new URLSearchParams();
    const m = encodeParent(male);
    const f = encodeParent(female);
    params.set("m", m);
    params.set("f", f);
    // Use the /api/share wrapper so social crawlers (X, Reddit, Slack,
    // iMessage) see OG/Twitter meta tags + a generated preview image.
    // Browsers redirect to /calculator?m=...&f=... where the SPA hydrates.
    return `${window.location.origin}/api/share?${params.toString()}`;
  }, [male, female]);

  // Side-effect helper: fire trending tracker for the current pair. Called
  // ONLY from explicit share actions (clipboard / X / Reddit / email) so a
  // simple re-render of the share URL preview doesn't inflate share_count.
  const trackCurrentPair = useCallback(() => {
    trackTrendingPair(encodeParent(male), encodeParent(female)).catch(() => {});
  }, [male, female]);

  const shareSummary = useMemo(() => {
    if (!results) {
      return "Check out this budgie breeding pair I built in Budgie DNA Lab.";
    }
    const top = [...results.outcomes]
      .sort((a, b) => b.probability - a.probability)
      .slice(0, 2)
      .map((o) => `${(o.probability * 100).toFixed(1)}% ${o.phenotype}`)
      .join(", ");
    return `Budgie pairing: ${results.male_summary} × ${results.female_summary} → ${top}.`;
  }, [results]);

  const onShare = async () => {
    const url = buildShareUrl();
    trackCurrentPair();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Share link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for environments without clipboard permission
      window.prompt("Copy this share link:", url);
    }
  };

  const onShareX = () => {
    const url = buildShareUrl();
    trackCurrentPair();
    const text = `${shareSummary} via @BudgieDNALab`;
    const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      text
    )}&url=${encodeURIComponent(url)}&hashtags=budgies,genetics`;
    window.open(intent, "_blank", "noopener,noreferrer");
  };

  const onShareReddit = () => {
    const url = buildShareUrl();
    trackCurrentPair();
    const title = shareSummary;
    const intent = `https://www.reddit.com/submit?url=${encodeURIComponent(
      url
    )}&title=${encodeURIComponent(title)}`;
    window.open(intent, "_blank", "noopener,noreferrer");
  };

  const onSendEmail = async () => {
    if (!emailTo || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTo)) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setEmailSending(true);
    try {
      await shareByEmail({
        recipient_email: emailTo,
        share_url: buildShareUrl(),
        summary: shareSummary,
        note: emailNote || undefined,
      });
      trackCurrentPair();
      toast.success(`Share link emailed to ${emailTo}`);
      setEmailOpen(false);
      setEmailTo("");
      setEmailNote("");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not send email. Please try again."
      );
    } finally {
      setEmailSending(false);
    }
  };

  const onAi = async (outcome) => {
    setAiOutcome(outcome);
    setAiOpen(true);
    setAiNarrative("");
    setAiLoading(true);
    try {
      const data = await chickNarrative({
        phenotype: outcome.phenotype,
        descriptor: outcome.descriptor,
        probability: outcome.probability,
        sex: outcome.sex,
      });
      setAiNarrative(data.narrative);
    } catch (e) {
      setAiNarrative(
        "Unable to generate notes right now. Please retry — every chick deserves its description."
      );
    } finally {
      setAiLoading(false);
    }
  };

  // AI chick portrait — called per outcome row. Returns a blob URL or throws.
  const onPortrait = useCallback(async (outcome) => {
    const sexWord = outcome.sex === "male" ? "cock" : outcome.sex === "female" ? "hen" : "chick";
    const descriptor = `${outcome.phenotype} ${sexWord}, ${outcome.descriptor || outcome.example_genotype}`;
    return await fetchBudgiePortrait({ descriptor, kind: "chick" });
  }, []);

  const totalProbability = useMemo(
    () => results?.outcomes.reduce((a, b) => a + b.probability, 0) ?? 0,
    [results]
  );

  return (
    <div data-testid="calculator-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <header className="mb-10">
        <div className="overline">Procedure 02 · Pair Selection</div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">
          Breeding Calculator
        </h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Configure each parent&apos;s mutations. The calculator combines all loci
          using Mendelian inheritance — autosomal dominance, incomplete
          dominance, recessive carriers and Z-linked sex inheritance — and
          returns the exact probability of every possible chick.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ParentEditor
          sex="male"
          parent={male}
          setParent={setMale}
          testIdPrefix="male"
          userPhotos={userPhotos}
          onUploadPhoto={handleUploadPhoto}
          onDeletePhoto={handleDeletePhoto}
        />
        <ParentEditor
          sex="female"
          parent={female}
          setParent={setFemale}
          testIdPrefix="female"
          userPhotos={userPhotos}
          onUploadPhoto={handleUploadPhoto}
          onDeletePhoto={handleDeletePhoto}
        />
      </div>

      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <button
          data-testid="run-breed-btn"
          disabled={loading}
          onClick={() => onRun()}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors font-medium disabled:opacity-50"
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : null}
          Compute offspring
        </button>
        <button
          data-testid="reset-btn"
          onClick={onReset}
          className="border border-border px-6 py-3 rounded-sm hover:bg-secondary transition-colors font-medium"
        >
          Reset
        </button>
        <button
          data-testid="share-link-btn"
          onClick={onShare}
          className="border border-border px-6 py-3 rounded-sm hover:bg-secondary transition-colors font-medium inline-flex items-center gap-2"
        >
          {copied ? <Check size={14} /> : <Link2 size={14} />}
          {copied ? "Link copied" : "Share link"}
        </button>
        <button
          data-testid="share-x-btn"
          onClick={onShareX}
          title="Share on X"
          aria-label="Share on X"
          className="border border-border px-4 py-3 rounded-sm hover:bg-secondary transition-colors font-medium inline-flex items-center gap-2"
        >
          <XLogo />
          <span className="hidden sm:inline">Post on X</span>
        </button>
        <button
          data-testid="share-reddit-btn"
          onClick={onShareReddit}
          title="Share on Reddit"
          aria-label="Share on Reddit"
          className="border border-border px-4 py-3 rounded-sm hover:bg-secondary transition-colors font-medium inline-flex items-center gap-2"
        >
          <RedditLogo />
          <span className="hidden sm:inline">Post to Reddit</span>
        </button>
        <button
          data-testid="share-email-btn"
          onClick={() => setEmailOpen(true)}
          title="Email this pair"
          aria-label="Email this pair"
          className="border border-border px-4 py-3 rounded-sm hover:bg-secondary transition-colors font-medium inline-flex items-center gap-2"
        >
          <Mail size={14} />
          <span className="hidden sm:inline">Email</span>
        </button>
        {error && (
          <div className="text-destructive flex items-center gap-2 text-sm">
            <AlertTriangle size={14} /> {error}
          </div>
        )}
      </div>

      {results && (
        <section
          data-testid="results-section"
          className="mt-12 border-t border-border pt-10"
        >
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <ParentSummaryCard
              role="sire"
              summary={results.male_summary}
              descriptor={`${results.male_summary} cock`}
            />
            <ParentSummaryCard
              role="dam"
              summary={results.female_summary}
              descriptor={`${results.female_summary} hen`}
            />
            <div className="border border-border p-5">
              <div className="overline">Outcome classes</div>
              <div className="text-lg font-bold mt-1">
                {results.outcomes.length} variants ·{" "}
                <span className="font-mono">
                  Σ {(totalProbability * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {results.outcomes.map((o, i) => (
              <OutcomeRow key={`${o.phenotype}-${i}`} outcome={o} onAi={onAi} onPortrait={onPortrait} index={i} />
            ))}
          </div>
        </section>
      )}

      <AiModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        outcome={aiOutcome}
        narrative={aiNarrative}
        loading={aiLoading}
      />

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="share-email-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Email this breeding pair</DialogTitle>
            <DialogDescription>
              We&apos;ll send the share link plus a short summary of the predicted outcomes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="block text-xs uppercase tracking-wider text-muted-foreground">
              Recipient
              <input
                data-testid="share-email-recipient"
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="friend@example.com"
                className="mt-1 w-full border border-border bg-background px-3 py-2 rounded-sm text-sm normal-case tracking-normal"
                autoFocus
              />
            </label>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground">
              Note (optional)
              <textarea
                data-testid="share-email-note"
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                rows={3}
                maxLength={600}
                placeholder="Thought you'd want to see this pairing…"
                className="mt-1 w-full border border-border bg-background px-3 py-2 rounded-sm text-sm normal-case tracking-normal"
              />
            </label>
            <div className="text-xs text-muted-foreground break-all border border-border px-3 py-2 rounded-sm">
              {buildShareUrl()}
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setEmailOpen(false)}
              className="border border-border px-4 py-2 rounded-sm text-sm hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              data-testid="share-email-send-btn"
              type="button"
              disabled={emailSending}
              onClick={onSendEmail}
              className="bg-foreground text-background px-4 py-2 rounded-sm text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
            >
              {emailSending ? <Loader2 className="animate-spin" size={14} /> : <Mail size={14} />}
              {emailSending ? "Sending…" : "Send email"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
